/* Built In Imports */
import { useEffect, useState } from 'react';

/* External Imports */
import {
  Box,
  CloseButton,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Text,
} from '@chakra-ui/react';
import { useMediaQuery } from 'react-responsive';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

/* Internal Imports */
/* Component */
import SelectUI from '@components/Elements/SelectUI';
import { phoneWithCountryCode } from '@constants/phoneWithCountryCode';
import { EnrollSectionSt } from '../../landing_pages/ieo/Common/CompLangConfig';
import { getCurrentRegion, pushLeadEnquiry } from 'services/commonService';
import { FiThumbsUp } from 'react-icons/fi';
import { mixPanel } from '@utils/Mixpanel';
import config from '@config';
import { usePathname } from 'next/navigation';
import { useSearchParams } from 'next/navigation';

/* Styles */
/* Services */

/**
 *
 * @param {Object} pageData
 * @param {String} lang
 * @param {String} region
 * @returns {ReactElement} PricingPopup
 */
const InlineEnquireForm = ({
  lang,
  region,
  isPopup,
  setIsPopup,
  pageData = {},
}) => {
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const pathname = usePathname();
  const [isLoading, setLoading] = useState(false);
  const [showResp, setShowResp] = useState(false);
  const [curCountry, setCurCountry] = useState('');
  const [curIpCountry, setCurIpCountry] = useState('');
  const Mixpanel = mixPanel(pathname);
  const isWavesOfBlissUPPage = pathname.includes('wave-of-bliss-uttar-pradesh');

  const dataJson = pageData?._allSharedJsonLocales?.[0]?.value?.json || [
    {},
    {},
    {},
  ];
  const [fieldName, fieldErrors, successMsg] = Array.isArray(dataJson)
    ? dataJson
    : [{}, {}, {}];
  const errorMessageObj = fieldErrors?.validationMessages || {};
  const successMessageObj = successMsg?.successMessage || {};

  const localData = async () => {
    const reg = await getCurrentRegion();

    if (reg?.country) {
      setCurCountry(reg?.country);
      setCurIpCountry(reg?.country);
    }
  };

  useEffect(() => {
    localData();
    Object.keys(phoneWithCountryCode).forEach(function (key, index) {
      phoneWithCountryCode[key]['label'] = `${
        phoneWithCountryCode[key]['iso2_code']
      } + (${phoneWithCountryCode[key]['phone_code']?.replace('+', '')})`;
      phoneWithCountryCode[key]['value'] =
        phoneWithCountryCode[key]['iso2_code'];
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && isPopup) {
      Mixpanel.track('ie_wave_enquiry_form_impression', {
        page_name: 'Inline form page',
        language: config?.lngList?.[lang],
      });
    }
  }, [isPopup]);

  phoneWithCountryCode.sort(function (a, b) {
    return a.label - b.label;
  });

  const initialVal = {
    fName: '',
    phoneCountryCode: '',
    mobile: '',
    postalCode: '',
    privacy: false,
  };

  const ValidationSchema = Yup.object().shape({
    fName: Yup.string()
      .matches(/^[a-zA-Z\s]+$/, {
        message:
          errorMessageObj?.fullname?.specialchar ||
          'Please do not use numbers or special characters',
      })
      .max(100, errorMessageObj?.fullname?.invalid || 'Full Name is invalid')
      .required(errorMessageObj?.fullname?.required || 'Full Name is required'),
    phoneCountryCode: Yup.string(),
    // .min(1, 'Invalid phone code')
    // .max(5, 'Invalid phone code')
    // .required('Phone code is required'),
    mobile: Yup.string()
      .min(
        curCountry === 'IN' ? 10 : 6,
        errorMessageObj?.mobilenumber?.invalid || 'Mobile number is invalid'
      )
      .max(
        curCountry === 'IN' ? 10 : 16,
        errorMessageObj?.mobilenumber?.invalid || 'Mobile number is invalid'
      )
      .required(
        errorMessageObj?.mobilenumber?.required || 'Mobile number is required'
      )
      .test(
        'not-all-zeros',
        errorMessageObj?.mobilenumber?.invalidno ||
          'Please enter a valid phone number',
        value => {
          if (!value) return true; // required() will handle emptiness
          return !/^0+$/.test(value); // ❌ reject if all digits are zero
        }
      )
      .when('phoneCountryCode', {
        is: 'IN',
        then: Yup.string().matches(
          /^\d{10}$/,
          errorMessageObj?.mobilenumber?.invalid || 'Mobile number is invalid'
        ),
        otherwise: Yup.string().matches(
          /^\d{6,16}$/,
          errorMessageObj?.mobilenumber?.invalid || 'Mobile number is invalid'
        ),
      }),
    postalCode: Yup.string()
      .matches(/^([1-9][0-9]{2}\s?[0-9]{3})$/u, {
        message: errorMessageObj?.pincode?.invalid || 'Pin Code is invalid',
      })
      .max(6, errorMessageObj?.pincode?.invalid || 'Pin Code is invalid')
      .required(errorMessageObj?.pincode?.required || 'Pin Code is required'),
    privacy: Yup.boolean()
      .oneOf(
        [true],
        errorMessageObj?.['terms&conditions']?.required ||
          'Please select Privacy Policy and Terms and Conditions'
      )
      .required('Please select Privacy Policy and Terms and Conditions'),
  });

  const onSubmit = async (values, { resetForm }) => {
    Mixpanel.track('ie_wave_enquiry_form_submitted', {
      page_name: 'Inline form page',
      language: config?.lngList?.[lang],
    });
    const { fName, postalCode, phoneCountryCode, mobile } = values;

    const phoneCountryOption =
      phoneWithCountryCode.find(item => item.iso2_code === curCountry)
        ?.phone_code || '';

    setLoading(true);
    const URLCamp = searchParams.get('utm_campaign')
    const leadsData = {
      message: {
        systemId: '110',
        programType: 'IE Wave',
        leadSource: 'Landing Page',
        name: fName,
        phone: `${phoneCountryOption} ${mobile}`.replace(/\s+/g, ''),
        whatsapp: '',
        email: '',
        jobTitle: '',
        companyName: '',
        country: curCountry,
        pincode: postalCode,
        utmCampaign: URLCamp ? URLCamp : isWavesOfBlissUPPage ? 'ie_wave_lucknow' : 'ie_wave_bengaluru',
        utmMedium:
          searchParams.get('utm_medium') || '',
        utmSource:
          searchParams.get('utm_source') || '',
        utmTerm: searchParams.get('utm_term') || '',
        utmContent: searchParams.get('utm_content') || '',
        utmReferer:
          searchParams.get('utmReferer') || '',
        programYear: new Date().getFullYear(),
        lang_code: lang ?? "",
      },
    };
    try {
      const response = await pushLeadEnquiry(leadsData);
      if (response) {
        resetForm(initialVal);
        setShowResp(true);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'ie_wave_lead_web_success',
          program_name: isWavesOfBlissUPPage ? 'IE Wave - UP' : 'IE Wave - BLR',
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    setIsPopup(false);
    setShowResp(false);
  };

  const getHeader = () => {
    return (
      <Flex
        bg="#B49168"
        p="20px"
        position={'relative'}
        borderRadius={{ base: '6px 6px 0px 0px', md: 'none' }}
        w={'100%'}
      >
        <Box
          fontSize="26px"
          color={'#fff'}
          fontWeight={500}
          textAlign={'center'}
          fontFamily="FedraSansStd-medium"
          w={'100%'}
        >
          {fieldName?.title || 'Enquiry Form'}
        </Box>
        <CloseButton
          _focusVisible={{ boxShadow: 'none' }}
          pos="absolute"
          size={'lg'}
          right={{ base: '0' }}
          display={{ md: 'flex' }}
          top="50%"
          transform="translate(-50%, -50%)"
          color="#fff"
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </Flex>
    );
  };

  return (
    <Drawer
      isOpen={isPopup}
      placement={isMobile ? 'bottom' : 'right'}
      onClose={onClose}
      size="full"
    >
      <DrawerOverlay />
      <DrawerContent
        background={'rgba(0, 0, 0, 0.1)'}
        h={{ base: 'auto', md: '100vh' }}
        mt={{ base: 'auto', md: '0' }}
      >
        <DrawerBody
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={{ base: '0px !important' }}
        >
          <Box
            maxW={{ base: '100%', md: '540px' }}
            mx="auto"
            width="100%"
            background="#ffffff"
            pos="relative"
            maxH="100%"
            overflowY="auto"
            px="0"
            py="0"
            borderRadius={{ base: '6px 6px 0px 0px', md: 'none' }}
          >
            {showResp ? (
              <>
                {getHeader()}
                <Flex
                  flexFlow={{ base: 'column' }}
                  pos="relative"
                  justifyContent="center"
                  w={'100%'}
                  gap={'10px'}
                  alignItems={'center'}
                  py={{ base: '134px', md: '164px' }}
                >
                  <FiThumbsUp color="#F37021" size={29} />
                  <Text
                    fontFamily="FedraSansStd-medium"
                    fontSize="20px"
                    color="#000000"
                    fontWeight={500}
                  >
                    {successMessageObj?.thankyou || 'Thank You'}
                  </Text>
                  <Text
                    fontFamily="FedraSansStd-book"
                    fontSize="16px"
                    fontWeight={400}
                    lineHeight="26px"
                    color="#000000"
                    pt="10px"
                    mx={'46px'}
                    textAlign={'center'}
                  >
                    {successMessageObj?.message ||
                      'A volunteer will get in touch with you shortly.'}
                  </Text>
                </Flex>
              </>
            ) : (
              <Flex
                flexFlow={{ base: 'column-reverse', md: 'row' }}
                flexWrap="wrap"
                pos="relative"
                justifyContent="center"
                w={'100%'}
              >
                <Formik
                  initialValues={initialVal}
                  validationSchema={ValidationSchema}
                  onSubmit={onSubmit}
                  w={{ base: '', sm: '', md: '' }}
                >
                  {({
                    values,
                    handleChange,
                    handleSubmit,
                    setFieldValue,
                    setFieldTouched,
                    setTouched,
                    errors,
                    isSubmitting,
                    isValid,
                    dirty,
                  }) => (
                    <Form onSubmit={handleSubmit} w={'100%'}>
                      {getHeader()}
                      <Box
                        display="flex"
                        gap={{ base: '11px', md: '14px' }}
                        flexWrap="wrap"
                        justifyContent="space-between"
                        maxW={{ base: 'auto', md: '644px' }}
                        mx={{ base: 'auto', lg: 'auto' }}
                        px={{ base: '14px', md: '60px' }}
                        pt={{ base: '16px', md: '41px' }}
                      >
                        <Field name="fName">
                          {({ field, form }) => (
                            <FormControl
                              isRequired
                              isInvalid={
                                form?.errors?.fName && form?.touched?.fName
                              }
                              width={{ base: '100%' }}
                            >
                              <Input
                                {...field}
                                id="fName"
                                type="text"
                                placeholder={fieldName?.name || 'Full Name *'}
                                _placeholder={{
                                  color: '#9B9685',
                                  fontWeight: '450',
                                  fontSize: '18px',
                                }}
                                color="#231F20"
                                borderRadius="0px"
                                bg="#F1EBDF"
                                border={'solid 1px'}
                                borderColor={'#F1EBDF'}
                                _hover={{
                                  borderColor: '#F1EBDF',
                                }}
                                _focus={{
                                  borderColor: '#F1EBDF',
                                }}
                                w={'100%'}
                                px={'27px'}
                                py={'6px'}
                                h={'52px'}
                                isInvalid={
                                  form?.errors?.fName && form?.touched?.fName
                                }
                                errorBorderColor={'#FF0000'}
                                {...EnrollSectionSt[lang]?.input}
                              />
                              <FormErrorMessage fontFamily="'Geist',sans-serif">
                                {form?.errors?.fName}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Box
                          display="flex"
                          w={{ base: '100%' }}
                          position="relative"
                        >
                          <Field name="phoneCountryCode">
                            {({ field, form }) => (
                              <FormControl
                                isRequired
                                isInvalid={
                                  form?.errors?.phoneCountryCode &&
                                  form?.touched?.phoneCountryCode
                                }
                                w="120px"
                              >
                                <SelectUI
                                  field={field}
                                  placeholder="+91"
                                  defaultValue={curCountry}
                                  id="phoneCountryCode"
                                  options={phoneWithCountryCode}
                                  onChange={event => {
                                    handleChange(event);
                                    setCurCountry(event.target.value);
                                  }}
                                  value={curCountry}
                                  colors={'#231F20'}
                                  borderRadius="0px"
                                  bg="#F1EBDF"
                                  border={'solid 1px'}
                                  borderColor={'#F1EBDF'}
                                  _hover={{
                                    borderColor: '#F1EBDF',
                                  }}
                                  _focus={{
                                    borderColor: '#F1EBDF',
                                  }}
                                  w={'100%'}
                                  h={'52px'}
                                  errorBorderColor={'#FF0000'}
                                  {...EnrollSectionSt[lang]?.input}
                                  padding="0 !important"
                                  isDisabled={curIpCountry === 'IN'}
                                  className="phoneCountryCodeClass"
                                />
                                <FormErrorMessage fontFamily="'Geist',sans-serif">
                                  {form?.errors?.phoneCountryCode}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                          <Box bg="#F1EBDF" maxH={'52px'}>
                            <Box
                              borderLeft="1px solid #9B9685 !important"
                              h="28px"
                              my="12px"
                            />
                          </Box>

                          <Field name="mobile">
                            {({ field, form }) => (
                              <FormControl
                                isRequired
                                isInvalid={
                                  form?.errors?.mobile && form?.touched?.mobile
                                }
                                width="calc(100% - 81px)"
                              >
                                <Input
                                  {...field}
                                  id="mobile"
                                  placeholder={
                                    fieldName?.phone || 'Mobile Number *'
                                  }
                                  _placeholder={{
                                    color: '#9B9685',
                                    fontWeight: '450',
                                    fontSize: '18px',
                                  }}
                                  color="#231F20"
                                  borderRadius="0px"
                                  bg="#F1EBDF"
                                  border={'solid 1px'}
                                  borderColor={'#F1EBDF'}
                                  _hover={{
                                    borderColor: '#F1EBDF',
                                  }}
                                  _focus={{
                                    borderColor: '#F1EBDF',
                                  }}
                                  w={'100%'}
                                  px={'27px'}
                                  py={'12px'}
                                  h={'52px'}
                                  isInvalid={
                                    form?.errors?.mobile &&
                                    form?.touched?.mobile
                                  }
                                  errorBorderColor={'#FF0000'}
                                  {...EnrollSectionSt[lang]?.input}
                                />
                                <FormErrorMessage fontFamily="'Geist',sans-serif">
                                  {form?.errors?.mobile}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </Box>

                        <Field name="postalCode">
                          {({ field, form }) => (
                            <FormControl
                              isRequired
                              isInvalid={
                                form?.errors?.postalCode &&
                                form?.touched?.postalCode
                              }
                              width={{ base: '100%' }}
                            >
                              <Input
                                {...field}
                                id="postalCode"
                                type="text"
                                placeholder={fieldName?.pincode || 'Pin Code *'}
                                _placeholder={{
                                  color: '#9B9685',
                                  fontWeight: '450',
                                  fontSize: '18px',
                                }}
                                color="#231F20"
                                borderRadius="0px"
                                bg="#F1EBDF"
                                border={'solid 1px'}
                                borderColor={'#F1EBDF'}
                                _hover={{
                                  borderColor: '#F1EBDF',
                                }}
                                _focus={{
                                  borderColor: '#F1EBDF',
                                }}
                                w={'100%'}
                                px={'27px'}
                                py={'6px'}
                                h={'52px'}
                                isInvalid={
                                  form?.errors?.postalCode &&
                                  form?.touched?.postalCode
                                }
                                errorBorderColor={'#FF0000'}
                                {...EnrollSectionSt[lang]?.input}
                              />
                              <FormErrorMessage fontFamily="'Geist',sans-serif">
                                {form?.errors?.postalCode}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="privacy">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form?.errors?.privacy && form?.touched?.privacy
                              }
                              isRequired
                              width="100%"
                              className="priv-check"
                              mt={{ base: '9px', md: '6px' }}
                            >
                              <Checkbox
                                checked={values.privacy}
                                {...field}
                                id="privacy"
                                variant="outline"
                                margin="0 10px 0 0"
                                borderColor="#535353"
                                colorScheme="red"
                                alignItems="top"
                                className="priv-check-class"
                              >
                                <Box
                                  color={'#676055'}
                                  fontSize={'14.43px'}
                                  fontFamily="'FedraSansStd-book', sans-serif"
                                  lineHeight={'22px'}
                                  mt={'-3px'}
                                >
                                  {fieldName?.terms1 ||
                                    'I have read, understood and agree to the'}{' '}
                                  <Box
                                    as="a"
                                    color={'#F37021 !important'}
                                    href={`${config.BASE_PATH}/in/en/copyright-privacy-policy-terms#privacy`}
                                    textDecorationLine="underline"
                                    target="_blank"
                                  >
                                    {fieldName?.terms2 || 'Privacy Policy'}
                                  </Box>{' '}
                                </Box>
                              </Checkbox>
                              <FormErrorMessage fontFamily="'Geist',sans-serif">
                                {form?.errors?.privacy}
                              </FormErrorMessage>
                              <style>
                                {`
                          .priv-check a {
                            color: #EC1C2D;
                            text-decoration: underline;
                          }`}
                              </style>
                            </FormControl>
                          )}
                        </Field>
                      </Box>
                      <Box
                        display="flex"
                        // ref={ref}
                        justifyContent="flex-start"
                        w={'max-content'}
                        mt={{ base: '20px', md: '31px' }}
                        mb={{ base: '45px', md: '63px' }}
                        mx={{ base: '14px', md: '60px' }}
                      >
                        <Button
                          p={'0px'}
                          px={'41px'}
                          type="submit"
                          w="full"
                          isLoading={isLoading}
                          fontSize={{
                            base: '18px',
                          }}
                          isDisabled={!isValid || !dirty || isLoading}
                          fontFamily="FedraSansStd-medium"
                          _active={{
                            border: 'none',
                            background: '#3652B8',
                          }}
                          margin="0 auto"
                          mb="0"
                          bgColor="#3652B8"
                          h={'auto'}
                          minH="60px"
                          textAlign="left"
                          borderRadius="55px"
                          color="#faf7f0"
                          textDecoration="none"
                          _hover={{
                            bg: '#3652B8',
                          }}
                        >
                          {fieldName?.submit || 'Submit'}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>

                <style jsx global>{`
                  ::placeholder {
                    color: #a1a1a1;
                  }
                  .phoneCountryCodeClass {
                    padding: 0 16px !important;
                  }
                  .priv-check-class {
                    span:first-child {
                      margin-top: 3px !important;
                    }
                  }
                `}</style>
              </Flex>
            )}
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default InlineEnquireForm;
